/**
 * Storezone — Button
 *
 * Variants: primary | secondary | ghost | danger | link
 * Sizes:    sm | md | lg
 */

import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react'
import { LoadingSpinner } from './LoadingSpinner'
import './Button.css'

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger' | 'link' | 'outline' | 'success'
  size?: 'sm' | 'md' | 'lg' | 'icon'
  loading?: boolean
  leftIcon?: ReactNode
  rightIcon?: ReactNode
  fullWidth?: boolean
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'md',
      loading = false,
      leftIcon,
      rightIcon,
      fullWidth = false,
      disabled,
      children,
      className = '',
      ...props
    },
    ref,
  ) => {
    const isDisabled = disabled || loading

    return (
      <button
        ref={ref}
        disabled={isDisabled}
        aria-disabled={isDisabled}
        aria-busy={loading}
        className={[
          'sz-btn',
          `sz-btn--${variant}`,
          `sz-btn--${size}`,
          fullWidth ? 'sz-btn--full' : '',
          loading ? 'sz-btn--loading' : '',
          className,
        ]
          .filter(Boolean)
          .join(' ')}
        {...props}
      >
        {loading && (
          <span className="sz-btn__spinner">
            <LoadingSpinner size="sm" />
          </span>
        )}
        {!loading && leftIcon && (
          <span className="sz-btn__icon sz-btn__icon--left">{leftIcon}</span>
        )}
        {children && <span className="sz-btn__label">{children}</span>}
        {!loading && rightIcon && (
          <span className="sz-btn__icon sz-btn__icon--right">{rightIcon}</span>
        )}
      </button>
    )
  },
)

Button.displayName = 'Button'
